import { useLocale } from '@mirai/locale';
import { Icon, Notification, styles, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import { RoomCard } from './Booking.RoomCard';
import { useSelectedRoomData } from './hooks';
import { getOccupationLabel, ICON, OCCUPATION_TYPE } from '../../helpers';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';
import { getDetailsByRoom, getOccupationFromParty } from '../helpers';

const { ADULT, CHILD } = OCCUPATION_TYPE;

const isRoomMatch = (id, room) => id === room;

const getSelectedRoomDetail = ({ details = [], room } = {}) => {
  if (!room) return;

  const detailsByRoom = getDetailsByRoom(details);
  const roomIndex = room.index;

  return roomIndex !== undefined
    ? detailsByRoom[roomIndex]
    : detailsByRoom.find(({ id } = {}) => id !== undefined && isRoomMatch(id, room.id));
};

const getOccupationText = (occupation = {}, translate = () => {}) => {
  const labels = [
    occupation.adults ? getOccupationLabel({ amount: occupation.adults, type: ADULT }, translate) : undefined,
    occupation.children ? getOccupationLabel({ amount: occupation.children, type: CHILD }, translate) : undefined,
  ].filter(Boolean);

  return labels.join(', ');
};

const FormEditOccupation = ({ dataSource = {}, responseOccupation = {}, onChange = () => {}, ...others }) => {
  const { isDesktop } = useDevice();
  const { currencyFormat, translate } = useLocale();
  const { details = [], modify: { occupation: { rooms = [] } = {} } = {}, price = {}, currency } = dataSource;
  const { selectedRoomData: { image, occupation, plan, rate, title } = {}, showSelectedRoom } = useSelectedRoomData({
    details,
    room: responseOccupation.room,
    rooms,
  });

  const viewData = useMemo(() => {
    const currentOccupation = getSelectedRoomDetail({ details, room: responseOccupation.room })?.occupation;
    const nextOccupation = getOccupationFromParty(responseOccupation.party);
    const currentPrice = price.value;
    const nextPrice = responseOccupation.price?.value;

    return {
      currency: responseOccupation.currency || currency,
      current: currentOccupation,
      currentPrice,
      next: nextOccupation,
      nextPrice,
      option: responseOccupation.option,
    };
  }, [currency, details, price.value, responseOccupation]);

  useEffect(() => {
    const roomId = responseOccupation.room?.id;

    onChange({
      option: viewData.option,
      price: viewData.nextPrice,
      ...(roomId !== undefined ? { roomId } : undefined),
    });
  }, [onChange, responseOccupation?.room, viewData.nextPrice, viewData.option]);

  const currentOccupationText = getOccupationText(viewData.current, translate);
  const nextOccupationText = getOccupationText(viewData.next, translate);
  const nextOccupationLines = nextOccupationText.split(', ').filter(Boolean);
  const comparison = (
    <View wide className={styles(style.summaryContainer, style.occupationComparison)}>
      <View className={styles(style.column, style.current)} testId="booking-current-occupation-summary">
        <Text bold headline={isDesktop}>
          {translate(L10N.LABEL_CURRENT_OCCUPATION)}
        </Text>
        <Text action>{currentOccupationText}</Text>
        <View wide>
          <Text bold>{translate(L10N.LABEL_TOTAL)}</Text>
          <Text bold headline>
            {currencyFormat({ currency: viewData.currency, value: viewData.currentPrice })}
          </Text>
          <Text small>{translate(L10N.LABEL_INCLUDES_TAXES)}</Text>
        </View>
      </View>

      <View className={style.column} testId="booking-new-occupation-summary">
        <Text bold headline={isDesktop}>
          {translate(L10N.LABEL_NEW_OCCUPATION)}
        </Text>
        <View row className={style.highlights}>
          {nextOccupationLines.length > 0 ? (
            nextOccupationLines.map((line) => (
              <View row key={line} className={style.highlight}>
                <Icon success value={ICON.SUCCESS} />
                <Text bold success>
                  {line}
                </Text>
              </View>
            ))
          ) : (
            <Text action>{nextOccupationText}</Text>
          )}
        </View>
        <View wide>
          <Text bold>{translate(L10N.LABEL_TOTAL)}</Text>
          <Text bold headline>
            {currencyFormat({ currency: viewData.currency, value: viewData.nextPrice })}
          </Text>
          <Text small>{translate(L10N.LABEL_INCLUDES_TAXES)}</Text>
        </View>
      </View>
      {responseOccupation?.adjustDifferenceAtProperty && (
        <View className={style.adjustDifferent}>
          <Notification info wide>
            {translate(L10N.NOTIFICATION_ADJUST_DIFFERENT)}
          </Notification>
        </View>
      )}
    </View>
  );

  return (
    <View {...others} wide className={styles(showSelectedRoom && style.formEditOccupation, others.className)}>
      {showSelectedRoom && (
        <RoomCard
          {...{ image, occupation, plan, rate, title }}
          className={style.selectedRoomCard}
          testId="booking-edit-occupation-room-card"
        />
      )}
      {comparison}
    </View>
  );
};

FormEditOccupation.displayName = 'Mirai:Core:Booking:FormEditOccupation';

FormEditOccupation.propTypes = {
  dataSource: PropTypes.shape({
    currency: PropTypes.string,
    details: PropTypes.arrayOf(PropTypes.shape({})),
    modify: PropTypes.shape({
      occupation: PropTypes.shape({
        rooms: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
          }),
        ),
      }),
    }),
    price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  responseOccupation: PropTypes.shape({
    adjustDifferenceAtProperty: PropTypes.bool,
    room: PropTypes.shape({
      id: PropTypes.string,
      index: PropTypes.number,
    }),
  }),
  onChange: PropTypes.func,
};

export { FormEditOccupation };
